import type { FragmentType } from '@/generated/gql/fragment-masking'
import { useFragment } from '@/generated/gql/fragment-masking'
import { graphql } from '@/generated/gql/gql'
import { capitalise } from '@/helpers/strings'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { Button, Menu, Typography, Stack, Skeleton } from '@mui/material'

import { isAfter } from 'date-fns/isAfter'
import { isBefore } from 'date-fns/isBefore'
import { isWithinInterval } from 'date-fns/isWithinInterval'
import { parseISO } from 'date-fns/parseISO'
import { startOfISOWeek } from 'date-fns/startOfISOWeek'
import type { MouseEvent } from 'react'
import { useEffect, useState } from 'react'

import { formatDateText } from './helpers'
import MealIcon from './MealIcon'
import { ServiceDate } from './ServiceDate'
import {
  formatServiceWeekDate,
  getServiceDateKey,
  getServiceWeekEnd,
  getServiceWeekStart,
  parseServiceDate,
  shiftServiceWeek,
} from './serviceWeeks'

const canteenServiceSelectionUserDataFragment = graphql(`
  fragment CanteenServiceSelection_UserDataFragment on User {
    accountLocation {
      timezone
    }
    upcomingCcMenus {
      date
      ...CanteenServiceDate_MealServiceDataFragment
    }
  }
`)

interface ServiceSelectionGraphqlProps {
  serviceData: FragmentType<typeof canteenServiceSelectionUserDataFragment>
  selectedDate: string
  selectedMeal: string
  onChangeSelectedService: (input: { meal: string; date: string }) => void
}

export function ServiceSelectionGraphql({
  selectedDate,
  selectedMeal,
  serviceData,
  onChangeSelectedService,
}: ServiceSelectionGraphqlProps) {
  const { accountLocation, upcomingCcMenus } = useFragment(
    canteenServiceSelectionUserDataFragment,
    serviceData,
  )

  const timezone = accountLocation?.timezone

  const [selectedWeekStart, setSelectedWeekStart] = useState(() => startOfISOWeek(new Date()))
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  useEffect(() => {
    if (!timezone) return
    setSelectedWeekStart(getServiceWeekStart(selectedDate, timezone))
  }, [timezone, selectedDate])

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleNextWeekSelect = () => {
    if (!timezone || !upcomingCcMenus || !upcomingCcMenus.length) return
    const latestWeekEndInData = getServiceWeekEnd(
      upcomingCcMenus[upcomingCcMenus.length - 1].date,
      timezone,
    )
    setSelectedWeekStart((prev) => {
      const nextWeekStart = shiftServiceWeek(prev, 1, timezone)
      if (isAfter(nextWeekStart, latestWeekEndInData)) return prev
      return nextWeekStart
    })
  }

  const handlePreviousWeekSelect = () => {
    if (!timezone || !upcomingCcMenus || !upcomingCcMenus.length) return
    const earliestWeekStartInData = getServiceWeekStart(upcomingCcMenus[0].date, timezone)
    setSelectedWeekStart((prev) => {
      const previousWeekStart = shiftServiceWeek(prev, -1, timezone)
      if (isBefore(previousWeekStart, earliestWeekStartInData)) return prev
      return previousWeekStart
    })
  }

  if (!timezone) {
    return (
      <Button
        fullWidth
        variant="outlined"
        disabled
        sx={{
          display: 'block',
          borderColor: 'divider',
          minHeight: '40px',
          boxShadow: 'none',
        }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Skeleton width="100%" />
          <ExpandMore color="action" />
        </Stack>
      </Button>
    )
  }

  const handleServiceChange = (date: Date, meal: string) => {
    handleClose()
    onChangeSelectedService({ date: getServiceDateKey(date, timezone) ?? 'Invalid date', meal })
  }

  const selectedDateInTz = parseServiceDate(selectedDate, timezone)
  const selectedDateKey = getServiceDateKey(selectedDateInTz, timezone)
  const selectedWeekEnd = getServiceWeekEnd(selectedWeekStart, timezone)
  const dataRangeToShow = upcomingCcMenus.filter((serviceDate) =>
    isWithinInterval(parseISO(serviceDate.date), {
      start: selectedWeekStart,
      end: selectedWeekEnd,
    }),
  )
  const open = Boolean(anchorEl)

  return (
    <>
      <Button
        onClick={handleClick}
        aria-haspopup="true"
        id="basic-button"
        variant="outlined"
        size="medium"
        fullWidth
        sx={{
          borderColor: 'divider',
          minHeight: '40px',
          boxShadow: 'none',
          overflow: 'hidden',
        }}
        startIcon={<MealIcon type={selectedMeal} />}
        endIcon={open ? <ExpandLess color="action" /> : <ExpandMore color="action" />}>
        <Typography noWrap paddingLeft={1} variant="body1">
          {formatDateText(selectedDateInTz, timezone)}
          {' ('}
          {capitalise(selectedMeal)}
          {') '}
        </Typography>
      </Button>
      <Menu
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          maxHeight: '400px',
        }}
        anchorEl={anchorEl}
        id="basic-menu"
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
          },
        }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          borderBottom={2}
          borderColor="primary.main">
          <Button
            sx={{ justifyContent: 'left' }}
            variant="text"
            onClick={() => handlePreviousWeekSelect()}>
            <ChevronLeft color="action" />
          </Button>
          <Typography>
            {formatServiceWeekDate(selectedWeekStart, timezone)} -{' '}
            {formatServiceWeekDate(selectedWeekEnd, timezone)}
          </Typography>
          <Button
            sx={{ justifyContent: 'right' }}
            variant="text"
            onClick={() => handleNextWeekSelect()}>
            <ChevronRight color="action" />
          </Button>
        </Stack>
        {dataRangeToShow.map((dataForDate) => (
          <ServiceDate
            key={dataForDate.date}
            onChange={handleServiceChange}
            isSelectedDate={
              selectedDateKey !== null &&
              getServiceDateKey(dataForDate.date, timezone) === selectedDateKey
            }
            selectedMeal={selectedMeal}
            serviceData={dataForDate}
            timezone={timezone}
          />
        ))}
      </Menu>
    </>
  )
}
